/* =============================================================
   Unified PDP gallery  (<product-gallery>)
   - Instant thumbnail switching (no network re-fetch, no re-init)
   - Keyboard accessible thumbnail tablist (roving tabindex + arrows)
   - Variant -> image sync (best effort, via embedded variant map)
   - Vanilla full-screen lightbox: swipe, pinch / double-tap zoom, pan
   No jQuery, no third-party libraries.
   ============================================================= */
(function () {
  'use strict';

  /* ---------------------------------------------------------
     Shared full-screen lightbox (one instance for the page)
     --------------------------------------------------------- */
  var Lightbox = {
    dialog: null,
    img: null,
    counter: null,
    items: [],
    index: 0,
    returnFocus: null,
    // zoom / pan state
    scale: 1,
    tx: 0,
    ty: 0,
    pointers: new Map(),
    pinchStartDist: 0,
    pinchStartScale: 1,
    panStartX: 0,
    panStartY: 0,
    panStartTx: 0,
    panStartTy: 0,
    swipeStartX: 0,
    swipeActive: false,

    build: function () {
      if (this.dialog) return;
      var dialog = document.createElement('dialog');
      dialog.className = 'pdp-lightbox';
      dialog.setAttribute('aria-label', 'Product image viewer');
      dialog.innerHTML =
        '<div class="pdp-lightbox__stage">' +
          '<img class="pdp-lightbox__img" alt="">' +
        '</div>' +
        '<button type="button" class="pdp-lightbox__close" aria-label="Close (Esc)">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<button type="button" class="pdp-lightbox__nav pdp-lightbox__nav--prev" aria-label="Previous image">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<button type="button" class="pdp-lightbox__nav pdp-lightbox__nav--next" aria-label="Next image">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<div class="pdp-lightbox__counter" aria-hidden="true"></div>';
      document.body.appendChild(dialog);

      this.dialog = dialog;
      this.img = dialog.querySelector('.pdp-lightbox__img');
      this.stage = dialog.querySelector('.pdp-lightbox__stage');
      this.counter = dialog.querySelector('.pdp-lightbox__counter');

      var self = this;
      dialog.querySelector('.pdp-lightbox__close').addEventListener('click', function () { self.close(); });
      dialog.querySelector('.pdp-lightbox__nav--prev').addEventListener('click', function (e) { e.stopPropagation(); self.go(-1); });
      dialog.querySelector('.pdp-lightbox__nav--next').addEventListener('click', function (e) { e.stopPropagation(); self.go(1); });

      // Click backdrop (outside the image) to close
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog || e.target === self.stage) self.close();
      });

      dialog.addEventListener('cancel', function () { /* native ESC -> close handler below */ });
      dialog.addEventListener('close', function () { self.afterClose(); });

      dialog.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); self.go(1); }
      });

      // Double click / double tap -> toggle zoom
      this.stage.addEventListener('dblclick', function (e) {
        e.preventDefault();
        self.toggleZoom(e.clientX, e.clientY);
      });

      this.bindGestures();
    },

    open: function (items, index, returnFocus) {
      this.build();
      this.items = items || [];
      if (!this.items.length) return;
      this.returnFocus = returnFocus || null;
      this.index = Math.max(0, Math.min(index || 0, this.items.length - 1));
      this.resetZoom();
      this.render();
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('pdp-lightbox-open');
    },

    render: function () {
      var item = this.items[this.index];
      if (!item) return;
      this.img.src = item.full;
      this.img.alt = item.alt || '';
      this.counter.textContent = (this.index + 1) + ' / ' + this.items.length;
      var multi = this.items.length > 1;
      this.dialog.querySelector('.pdp-lightbox__nav--prev').hidden = !multi;
      this.dialog.querySelector('.pdp-lightbox__nav--next').hidden = !multi;
    },

    go: function (delta) {
      if (this.items.length < 2) return;
      this.index = (this.index + delta + this.items.length) % this.items.length;
      this.resetZoom();
      this.render();
    },

    close: function () {
      if (!this.dialog) return;
      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.afterClose();
      }
    },

    afterClose: function () {
      document.documentElement.classList.remove('pdp-lightbox-open');
      this.resetZoom();
      if (this.returnFocus && typeof this.returnFocus.focus === 'function') {
        this.returnFocus.focus();
      }
      this.returnFocus = null;
    },

    /* ---- zoom / pan ---- */
    resetZoom: function () {
      this.scale = 1; this.tx = 0; this.ty = 0;
      this.pointers.clear();
      this.applyTransform();
    },

    applyTransform: function () {
      if (!this.img) return;
      this.img.style.transform = 'translate(' + this.tx + 'px,' + this.ty + 'px) scale(' + this.scale + ')';
      this.img.classList.toggle('is-zoomed', this.scale > 1.01);
    },

    toggleZoom: function (cx, cy) {
      if (this.scale > 1.01) {
        this.resetZoom();
      } else {
        this.scale = 2.5;
        // zoom toward the pointer
        var rect = this.img.getBoundingClientRect();
        var originX = cx - (rect.left + rect.width / 2);
        var originY = cy - (rect.top + rect.height / 2);
        this.tx = -originX * (this.scale - 1);
        this.ty = -originY * (this.scale - 1);
        this.clampPan();
        this.applyTransform();
      }
    },

    clampPan: function () {
      if (!this.img) return;
      var rect = this.img.getBoundingClientRect();
      var maxX = Math.max(0, (rect.width - this.stage.clientWidth) / 2 + 20);
      var maxY = Math.max(0, (rect.height - this.stage.clientHeight) / 2 + 20);
      this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
      this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
    },

    bindGestures: function () {
      var self = this;
      var stage = this.stage;

      stage.addEventListener('pointerdown', function (e) {
        stage.setPointerCapture(e.pointerId);
        self.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (self.pointers.size === 2) {
          var pts = Array.from(self.pointers.values());
          self.pinchStartDist = self.dist(pts[0], pts[1]);
          self.pinchStartScale = self.scale;
          self.swipeActive = false;
        } else if (self.pointers.size === 1) {
          if (self.scale > 1.01) {
            // pan
            self.panStartX = e.clientX; self.panStartY = e.clientY;
            self.panStartTx = self.tx; self.panStartTy = self.ty;
          } else {
            // swipe to navigate
            self.swipeStartX = e.clientX;
            self.swipeActive = true;
          }
        }
      });

      stage.addEventListener('pointermove', function (e) {
        if (!self.pointers.has(e.pointerId)) return;
        self.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (self.pointers.size === 2) {
          var pts = Array.from(self.pointers.values());
          var d = self.dist(pts[0], pts[1]);
          if (self.pinchStartDist > 0) {
            self.scale = Math.max(1, Math.min(4, self.pinchStartScale * (d / self.pinchStartDist)));
            self.clampPan();
            self.applyTransform();
          }
        } else if (self.pointers.size === 1) {
          if (self.scale > 1.01) {
            self.tx = self.panStartTx + (e.clientX - self.panStartX);
            self.ty = self.panStartTy + (e.clientY - self.panStartY);
            self.clampPan();
            self.applyTransform();
          }
        }
      });

      var endPointer = function (e) {
        if (self.swipeActive && self.scale <= 1.01 && self.pointers.size === 1) {
          var dx = e.clientX - self.swipeStartX;
          if (Math.abs(dx) > 60) { self.go(dx < 0 ? 1 : -1); }
        }
        self.swipeActive = false;
        self.pointers.delete(e.pointerId);
        if (self.pointers.size < 2) self.pinchStartDist = 0;
        // Pinch -> single-finger pan hand-off: re-seed the pan baseline from the
        // surviving pointer so the next move computes a ~0 delta (no jump).
        if (self.pointers.size === 1 && self.scale > 1.01) {
          var survivor = Array.from(self.pointers.values())[0];
          self.panStartX = survivor.x;
          self.panStartY = survivor.y;
          self.panStartTx = self.tx;
          self.panStartTy = self.ty;
        }
        if (self.scale <= 1.01) { self.tx = 0; self.ty = 0; self.applyTransform(); }
      };
      stage.addEventListener('pointerup', endPointer);
      stage.addEventListener('pointercancel', endPointer);
    },

    dist: function (a, b) {
      var dx = a.x - b.x, dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }
  };

  /* ---------------------------------------------------------
     <product-gallery> custom element
     --------------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.slides = Array.from(this.querySelectorAll('.pdp-gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.pdp-gallery__thumb'));
      this.lightboxEnabled = this.getAttribute('data-lightbox') === 'true';

      this.activeIndex = this.slides.findIndex((s) => s.classList.contains('is-active'));
      if (this.activeIndex < 0) this.activeIndex = 0;

      this.bindThumbs();
      this.bindNav();
      this.bindOpen();
      this.bindVariants();
    }

    select(index, focusThumb) {
      if (index < 0 || index >= this.slides.length || index === this.activeIndex) {
        if (index === this.activeIndex && focusThumb && this.thumbs[index]) this.thumbs[index].focus();
        return;
      }
      const prev = this.slides[this.activeIndex];
      const next = this.slides[index];
      if (prev) { prev.hidden = true; prev.classList.remove('is-active'); }
      if (next) { next.hidden = false; next.classList.add('is-active'); }

      this.pauseMedia(prev);

      this.thumbs.forEach((t, i) => {
        const on = i === index;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });

      const activeThumb = this.thumbs[index];
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        if (focusThumb) activeThumb.focus();
      }
      this.activeIndex = index;
    }

    pauseMedia(slide) {
      if (!slide) return;
      const v = slide.querySelector('video');
      if (v && typeof v.pause === 'function') v.pause();
    }

    bindThumbs() {
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.select(i));
        thumb.addEventListener('keydown', (e) => {
          const last = this.thumbs.length - 1;
          let target = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') target = Math.min(last, i + 1);
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') target = Math.max(0, i - 1);
          else if (e.key === 'Home') target = 0;
          else if (e.key === 'End') target = last;
          if (target !== null) { e.preventDefault(); this.select(target, true); }
        });
      });
    }

    bindNav() {
      const prev = this.querySelector('.pdp-gallery__nav--prev');
      const next = this.querySelector('.pdp-gallery__nav--next');
      if (prev) prev.addEventListener('click', () => {
        this.select((this.activeIndex - 1 + this.slides.length) % this.slides.length);
      });
      if (next) next.addEventListener('click', () => {
        this.select((this.activeIndex + 1) % this.slides.length);
      });
    }

    bindOpen() {
      if (!this.lightboxEnabled) return;
      this.querySelectorAll('.pdp-gallery__open').forEach((btn) => {
        btn.addEventListener('click', () => this.openLightbox(btn));
      });
    }

    openLightbox(trigger) {
      // Build the image-only list and find the index of the clicked slide within it.
      const items = [];
      let startIndex = 0;
      const clickedSlide = trigger.closest('.pdp-gallery__slide');
      this.slides.forEach((slide) => {
        if (slide.getAttribute('data-media-type') !== 'image') return;
        const openBtn = slide.querySelector('.pdp-gallery__open');
        if (!openBtn) return;
        if (slide === clickedSlide) startIndex = items.length;
        const imgEl = openBtn.querySelector('img');
        items.push({
          full: openBtn.getAttribute('data-full'),
          alt: imgEl ? imgEl.getAttribute('alt') : ''
        });
      });
      if (items.length) Lightbox.open(items, startIndex, trigger);
    }

    /* Variant -> image sync (best effort).
       Reads the embedded variantId -> mediaId map and watches the product
       form's [name="id"] input. Degrades gracefully when either is absent. */
    bindVariants() {
      const mapEl = this.querySelector('.pdp-gallery__variant-media');
      if (!mapEl) return;
      let map;
      try { map = JSON.parse(mapEl.textContent || '{}'); } catch (e) { return; }
      this.variantMedia = map;
      if (!Object.keys(map).length) return;

      this.variantScope = this.closest('[data-section-id]') ||
        this.closest('.product-template') ||
        this.closest('section') ||
        document;

      this.onVariantChange = (e) => {
        const t = e.target;
        if (!t || t.name !== 'id') return;
        this.syncVariant(t.value);
      };
      this.variantScope.addEventListener('change', this.onVariantChange);

      // Sync once on load in case a variant is preselected.
      const initial = this.variantScope.querySelector('[name="id"]');
      if (initial && initial.value) this.syncVariant(initial.value);
    }

    disconnectedCallback() {
      if (this.variantScope && this.onVariantChange) {
        this.variantScope.removeEventListener('change', this.onVariantChange);
      }
    }

    syncVariant(variantId) {
      if (!this.variantMedia) return;
      const mediaId = this.variantMedia[variantId];
      if (!mediaId) return;
      const idx = this.slides.findIndex((s) => s.getAttribute('data-media-id') === String(mediaId));
      if (idx >= 0) this.select(idx);
    }
  }

  if (!window.customElements.get('product-gallery')) {
    window.customElements.define('product-gallery', ProductGallery);
  }
})();
